"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { AnimatePresence, motion } from "motion/react";
import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "@/lib/firebase";
import { startPaymentForBooking } from "@/lib/payment";
import { loadDraft, saveDraft, clearDraft } from "@/lib/draftStorage";
import { SHOW_PAYMENT_STEP } from "@/lib/featureFlags";
import DemoPaymentStep from "@/components/DemoPaymentStep";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FormSplitLayout from "@/components/FormSplitLayout";
import type { Coach, Service } from "@/lib/types";
import {
  trackViewItem,
  trackBeginCheckout,
  trackAddPaymentInfo,
  trackPurchase,
} from "@/lib/analytics";
import { useTranslations } from "@/i18n/LocaleProvider";
import type { Step } from "./types";
import StepIndicator from "./steps/StepIndicator";
import StepHeader from "./steps/StepHeader";
import ServiceStep from "./steps/ServiceStep";
import SlotStep from "./steps/SlotStep";
import DetailsStep from "./steps/DetailsStep";
import PaymentStep from "./steps/PaymentStep";
import ConfirmedStep from "./steps/ConfirmedStep";

interface AvailableSlotsResponse {
  slots: number[];
}

interface CreateHoldResponse {
  booking: { id: string; reference: string; depositAmount: number };
  holdToken: string;
}

const DRAFT_KEY = "hy_book_draft_v1";

// What survives a refresh -- selectedService/selectedCoach are re-resolved from a fresh
// Firestore read rather than persisted as full objects, same reasoning as
// useSiteNavigation.ts's cache: stale nested data (a price change, a deactivated service)
// should never silently outlive the page load that fetched it.
interface BookDraft {
  step: Step;
  selectedServiceId: string | null;
  selectedDate: string;
  selectedSlot: number | null;
  name: string;
  email: string;
  emailVerified: boolean;
  phone: string;
  phoneVerified: boolean;
  termsAccepted: boolean;
  marketingOptIn: boolean;
  couponCode: string;
  bookingId: string | null;
  bookingReference: string | null;
  holdToken: string | null;
  demoOrder: { gatewayOrderId: string; amountRupees: number } | null;
}

// A Date's own getDate()/getDay() reason in the browser's LOCAL calendar, but toISOString()
// always reports the UTC one -- for any visitor west of UTC-that-day (India included, for the
// ~5.5 hours after local midnight where the UTC calendar hasn't rolled over yet), round-tripping
// a locally-computed date through toISOString().slice(0, 10) silently reports the day BEFORE the
// one actually computed. Every date this file hands to the backend is built from local
// getFullYear/getMonth/getDate and serialized with this instead, never toISOString().
function toDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

// Tomorrow, or the next weekday after that if tomorrow's a weekend -- coaches' schedules in
// this app are Mon-Fri (see functions/src/scripts/seed.ts), so defaulting to "today" routinely
// opened this step on a day with zero real slots to show.
function nextWeekday(): string {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  while (date.getDay() === 0 || date.getDay() === 6) {
    date.setDate(date.getDate() + 1);
  }
  return toDateString(date);
}

export default function BookingFlow() {
  const searchParams = useSearchParams();
  const initialServiceSlug = searchParams.get("service") ?? undefined;
  // Set only by the header's "Book a session" link (see Header.tsx) -- a deliberate "start a
  // new booking" click, as opposed to a refresh or the browser back/forward buttons landing on
  // this same /book URL, which should still resume a saved draft as always.
  const startFresh = searchParams.get("fresh") === "1";
  const router = useRouter();
  const { t } = useTranslations();

  // Every field below starts at the same plain default on both server and client -- reading
  // sessionStorage straight into a lazy useState initializer would make the client's very first
  // render disagree with the server-rendered HTML (the server has no sessionStorage) and trip a
  // hydration mismatch. Restoration instead happens in the effect below, which only ever runs
  // client-side, after hydration -- a same-tick correction, not a visible flash in practice.
  const [step, setStep] = useState<Step>("service");
  // Distinguishes "still fetching" from "fetched, and there really are none" -- services.length
  // === 0 is true during the brief window before the initial fetch resolves too, and without
  // this, ServiceStep's real empty-state copy (a dev/setup message, not customer-facing) would
  // flash on screen for every visitor on every load.
  const [isLoadingServices, setIsLoadingServices] = useState(true);
  const [services, setServices] = useState<Service[]>([]);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedCoach, setSelectedCoach] = useState<Coach | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(nextWeekday());
  // Starts true and stays true until the customer touches the date picker themselves -- lets
  // the slot-fetch effect below silently walk forward past days with nothing open (a weekend,
  // a holiday, an already fully-booked day) instead of defaulting blind to "tomorrow" and
  // showing an empty slot-list on arrival. Turned off the moment they pick a date on their own,
  // so an intentional "show me this specific day" click never gets silently overridden.
  const [autoFindingDate, setAutoFindingDate] = useState(true);
  const [slots, setSlots] = useState<number[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [phone, setPhone] = useState("");
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [couponCode, setCouponCode] = useState("");

  const [bookingId, setBookingId] = useState<string | null>(null);
  const [bookingReference, setBookingReference] = useState<string | null>(null);
  const [holdToken, setHoldToken] = useState<string | null>(null);
  const [demoOrder, setDemoOrder] = useState<{ gatewayOrderId: string; amountRupees: number } | null>(
    null
  );

  // Gates the persistence-writing effect below until the restore effect has actually run once
  // -- without this, that effect fires on the very first render (before the async restore
  // below has applied anything) and persists the plain pre-restore defaults, clobbering the
  // real saved draft it was about to read. A ref, not state, since flipping it shouldn't itself
  // trigger a render.
  const restoredRef = useRef(false);

  // Locks the step-content area to the tallest step height seen so far so switching steps
  // (e.g. Details, the tallest, back to Slot, much shorter) doesn't visibly shrink/grow the
  // form and the surrounding split-layout column -- never shrinks once grown, only grows to
  // fit a newly-visited taller step. A ResizeObserver (not a step-keyed effect) so a step's
  // own async content settling in later -- e.g. ServiceStep's list arriving after its initial
  // loading spinner -- still gets captured, instead of only ever seeing whatever was on screen
  // the instant the step first mounted.
  const stepContainerRef = useRef<HTMLDivElement>(null);
  const [minStepHeight, setMinStepHeight] = useState<number | undefined>(undefined);
  useEffect(() => {
    const el = stepContainerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      const height = entry.contentRect.height;
      setMinStepHeight((prev) => (prev === undefined ? height : Math.max(prev, height)));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Services and the (single) coach load together on mount -- this is a solo-coach business,
  // so there's never a real choice to make and no separate "choose your coach" step exists.
  // That step used to briefly flash on screen while this fetch was in flight before
  // auto-skipping to slots; loading it up front instead of after picking a service removes the
  // flicker entirely rather than just hiding the step visually. The same client-only mount also
  // restores a saved draft (see draftStorage.ts) -- combined here rather than a separate effect
  // since resolving the restored service needs the freshly-fetched service list either way.
  useEffect(() => {
    const restored = startFresh ? null : loadDraft<BookDraft>(DRAFT_KEY);
    if (startFresh) {
      clearDraft(DRAFT_KEY);
      // Scrubs ?fresh=1 off the URL so a later plain refresh of this same tab -- no longer
      // carrying the param -- goes back to resuming a draft rather than wiping it again.
      router.replace("/book");
    }

    (async () => {
      const [servicesSnap, coachesSnap] = await Promise.all([
        getDocs(query(collection(db, "services"), where("isActive", "==", true))),
        getDocs(query(collection(db, "coaches"), where("isActive", "==", true))),
      ]);

      // Applied after the await above (not synchronously at the top of the effect) so this
      // doesn't trip the project's react-hooks/set-state-in-effect rule -- see fetchSlots'
      // comment further down for the same reasoning applied elsewhere in this file.
      if (restored) {
        setStep(restored.step ?? "service");
        setSelectedDate(restored.selectedDate ?? nextWeekday());
        setSelectedSlot(restored.selectedSlot ?? null);
        setName(restored.name ?? "");
        setEmail(restored.email ?? "");
        setEmailVerified(restored.emailVerified ?? false);
        setPhone(restored.phone ?? "");
        setPhoneVerified(restored.phoneVerified ?? false);
        setTermsAccepted(restored.termsAccepted ?? false);
        setMarketingOptIn(restored.marketingOptIn ?? false);
        setCouponCode(restored.couponCode ?? "");
        setBookingId(restored.bookingId ?? null);
        setBookingReference(restored.bookingReference ?? null);
        setHoldToken(restored.holdToken ?? null);
        setDemoOrder(restored.demoOrder ?? null);
      }

      const loadedServices = servicesSnap.docs.map(
        (d) => ({ id: d.id, ...d.data() }) as Service
      );
      const loadedCoaches = coachesSnap.docs.map((d) => ({ id: d.id, ...d.data() }) as Coach);
      setServices(loadedServices);
      if (loadedCoaches.length === 1) {
        setSelectedCoach(loadedCoaches[0]);
      }

      // A restored draft's service normally takes priority over the URL slug -- it reflects
      // where the customer actually left off. But an explicit `?service=` link for a
      // DIFFERENT service than the saved draft means they deliberately started booking
      // something else (e.g. went back to /services and picked a different session) -- that
      // intent should win, and the stale draft for the old service should be dropped rather
      // than silently resurface later.
      const restoredService = restored?.selectedServiceId
        ? loadedServices.find((s) => s.id === restored.selectedServiceId)
        : undefined;
      const urlService = initialServiceSlug
        ? loadedServices.find((s) => s.slug === initialServiceSlug)
        : undefined;
      const conflictingIntent = Boolean(urlService && restoredService && urlService.id !== restoredService.id);
      if (conflictingIntent) {
        clearDraft(DRAFT_KEY);
      }
      const match = conflictingIntent ? urlService : (restoredService ?? urlService);
      if (match) {
        setSelectedService(match);
        // Only auto-advance past the service step for a fresh `?service=` link (or one that
        // just overrode a stale draft) -- a same-service restored draft already carries its
        // own (possibly further-along) step, and shouldn't be dragged back to "slot".
        if (conflictingIntent || !restored || restored.step === "service") {
          setStep("slot");
        }
      }
      restoredRef.current = true;
      setIsLoadingServices(false);
    })().catch((err) => {
      setError(String(err));
      setIsLoadingServices(false);
    });
    // router is stable across renders; startFresh deliberately excluded -- router.replace above
    // flips it back to false on the next render, and re-running this whole mount effect at that
    // point would just redundantly refetch services/coaches for no behavioral change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialServiceSlug]);

  // Persists on every change so a refresh (or an accidental tab close on mobile) resumes
  // exactly where the customer left off, instead of restarting the whole flow. Cleared once
  // confirmed -- there's nothing left to resume, and a stale confirmed draft has no use.
  useEffect(() => {
    if (!restoredRef.current) return;
    if (step === "confirmed") {
      clearDraft(DRAFT_KEY);
      return;
    }
    saveDraft<BookDraft>(DRAFT_KEY, {
      step,
      selectedServiceId: selectedService?.id ?? null,
      selectedDate,
      selectedSlot,
      name,
      email,
      emailVerified,
      phone,
      phoneVerified,
      termsAccepted,
      marketingOptIn,
      couponCode,
      bookingId,
      bookingReference,
      holdToken,
      demoOrder,
    });
  }, [
    step,
    selectedService,
    selectedDate,
    selectedSlot,
    name,
    email,
    emailVerified,
    phone,
    phoneVerified,
    termsAccepted,
    marketingOptIn,
    couponCode,
    bookingId,
    bookingReference,
    holdToken,
    demoOrder,
  ]);

  const maxDate = useMemo(() => {
    if (!selectedService) return undefined;
    const d = new Date();
    d.setDate(d.getDate() + selectedService.maxAdvanceDays);
    return toDateString(d);
  }, [selectedService]);

  // Returns data rather than setting state itself, so the effect below (the only caller) can
  // do its own setState in a local function -- calling a function that sets state directly
  // inside an effect body trips react-hooks/set-state-in-effect.
  async function fetchSlots(): Promise<number[]> {
    if (!selectedService || !selectedCoach) return [];
    const getAvailableSlots = httpsCallable<
      { serviceId: string; coachId: string; date: string },
      AvailableSlotsResponse
    >(functions, "getAvailableSlots");
    const result = await getAvailableSlots({
      serviceId: selectedService.id,
      coachId: selectedCoach.id,
      date: selectedDate,
    });
    return result.data.slots;
  }

  // Fetches automatically whenever the date changes on the slot step, instead of requiring an
  // explicit "check availability" click -- one less step between picking a date and seeing
  // times.
  useEffect(() => {
    if (step !== "slot" || !selectedCoach) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchSlots();
        if (cancelled) return;
        // Still searching for the first open day, this one came back empty, and there's more
        // road ahead -- advance a day and let this same effect re-run for it, rather than
        // showing the customer a dead end on the very first screen they see. Leaves `loading`
        // true across every probed day so this reads as one search, not a flash of "nothing
        // open" per day.
        if (result.length === 0 && autoFindingDate && maxDate && selectedDate < maxDate) {
          const next = new Date(`${selectedDate}T00:00:00`);
          next.setDate(next.getDate() + 1);
          setSelectedDate(toDateString(next));
          return;
        }
        setSlots(result);
      } catch (err) {
        if (cancelled) return;
        setError("Could not load availability. Please try again.");
        console.error(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, selectedDate, selectedCoach]);

  async function handleCreateHold() {
    if (!selectedService || !selectedCoach || !selectedSlot) return;
    setLoading(true);
    setError(null);
    try {
      // No ensureSignedIn() here anymore -- verifying the phone (PhoneOtpAuth, inside
      // DetailsStep) already signed the customer in via a custom token, see
      // functions/src/otp.ts's verifyPhoneOtpAndSignIn. There's no separate anonymous session
      // to establish, and createHold itself re-checks the verification server-side regardless.
      trackBeginCheckout({
        item_id: selectedService.id,
        item_name: selectedService.name,
        price: selectedService.priceInr,
      });

      const createHold = httpsCallable<
        {
          customerEmail: string | null;
          customerName: string;
          customerPhone: string;
          serviceId: string;
          coachId: string;
          startsAt: number;
          deliveryMode: "online" | "in_person" | null;
          termsAccepted: true;
          marketingOptIn: boolean;
          couponCode: string | null;
        },
        CreateHoldResponse
      >(functions, "createHold");

      const result = await createHold({
        customerEmail: email.trim() || null,
        customerName: name,
        customerPhone: phone,
        serviceId: selectedService.id,
        coachId: selectedCoach.id,
        startsAt: selectedSlot,
        deliveryMode: "online",
        termsAccepted: true,
        marketingOptIn,
        couponCode: couponCode.trim() || null,
      });

      setBookingId(result.data.booking.id);
      setBookingReference(result.data.booking.reference);
      setHoldToken(result.data.holdToken);
      if (SHOW_PAYMENT_STEP) {
        setStep("payment");
      } else {
        // Devina asked to skip a visible payment step for now (see featureFlags.ts) -- still
        // runs the exact same real payment call and demo-confirm this app would show a button
        // for, just without a customer-facing click in between.
        await handlePay({
          bookingId: result.data.booking.id,
          bookingReference: result.data.booking.reference,
          holdToken: result.data.holdToken,
        });
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Could not hold this slot. Please try another.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  async function handlePay(override?: {
    bookingId: string;
    bookingReference: string;
    holdToken: string;
  }) {
    const holdId = override?.bookingId ?? bookingId;
    const holdReference = override?.bookingReference ?? bookingReference;
    const token = override?.holdToken ?? holdToken;
    if (!holdId || !holdReference || !token || !selectedService) return;
    setLoading(true);
    setError(null);
    try {
      // Still signed in from the phone verification step in "details" -- see handleCreateHold.
      trackAddPaymentInfo({
        item_id: selectedService.id,
        item_name: selectedService.name,
        price: selectedService.priceInr,
      });

      const outcome = await startPaymentForBooking({
        bookingId: holdId,
        holdToken: token,
        description: `Deposit for booking ${holdReference}`,
        customerEmail: email,
        customerPhone: phone,
      });

      if (outcome.kind === "manual_confirmed") {
        // No gateway configured -- the booking is already confirmed server-side, payment is
        // arranged manually over WhatsApp. Nothing left to do on this page.
        trackPurchase({
          transactionId: holdId,
          value: selectedService.priceInr,
          item: { item_id: selectedService.id, item_name: selectedService.name, price: selectedService.priceInr },
        });
        setStep("confirmed");
        return;
      }

      if (outcome.kind === "needs_confirmation_redirect") {
        // In-page overlay gateways (Razorpay/Paytm) resolved inside startPaymentForBooking --
        // send the customer to the same webhook-driven confirmation page every gateway uses.
        router.push(`/book/confirmation?order_id=${holdId}`);
        return;
      }

      if (outcome.kind === "demo_pending") {
        if (SHOW_PAYMENT_STEP) {
          setDemoOrder({ gatewayOrderId: outcome.gatewayOrderId, amountRupees: outcome.amountRupees });
          setStep("demo_payment");
        } else {
          const confirmDemoPayment = httpsCallable<
            { gatewayOrderId: string; holdToken: string },
            { ok: true }
          >(functions, "confirmDemoPayment");
          await confirmDemoPayment({ gatewayOrderId: outcome.gatewayOrderId, holdToken: token });
          trackPurchase({
            transactionId: holdId,
            value: selectedService.priceInr,
            item: { item_id: selectedService.id, item_name: selectedService.name, price: selectedService.priceInr },
          });
          setStep("confirmed");
        }
      }
      // outcome.kind === "navigating_away": Cashfree/redirect gateways already left this page.
    } catch (err) {
      setError("Could not complete this booking. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Header />
      <FormSplitLayout image="/images/devina/photo-31.jpg" imageAlt="Devina in dancer pose on a tree-lined path">
      <div className="mx-auto w-full max-w-lg">
      {/* One shared header, driven by the current step -- rendered above StepIndicator so the
          title/back button always sit above the progress bar, on every step. */}
      {step === "service" && <StepHeader title={t("book.service.title")} />}
      {step === "slot" && (
        <StepHeader title={t("book.slot.title")} onBack={() => setStep("service")} backLabel={t("book.back")} />
      )}
      {step === "details" && (
        <StepHeader title={t("book.details.title")} onBack={() => setStep("slot")} backLabel={t("book.back")} />
      )}
      {step === "payment" && <StepHeader title={t("book.payment.title")} size="large" />}
      {step === "demo_payment" && <StepHeader title={t("demoPayment.title")} size="large" />}
      {step === "confirmed" && <StepHeader title={t("book.confirmed.title")} size="large" />}
      <div className="mt-4 sm:mt-5">
        <StepIndicator step={step} />
      </div>

      {error && (
        <p className="mt-6 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>
      )}

      <div ref={stepContainerRef} style={{ minHeight: minStepHeight }}>
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -16 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
        >
          {step === "service" && (
            <ServiceStep
              services={services}
              loading={isLoadingServices}
              onSelect={(service) => {
                setSelectedService(service);
                setStep("slot");
                trackViewItem({
                  item_id: service.id,
                  item_name: service.name,
                  price: service.priceInr,
                });
              }}
            />
          )}

          {step === "slot" && selectedService && selectedCoach && (
            <SlotStep
              selectedDate={selectedDate}
              onDateChange={(date) => {
                setAutoFindingDate(false);
                setSelectedDate(date);
                setSlots([]);
                setSelectedSlot(null);
              }}
              maxDate={maxDate}
              loading={loading}
              slots={slots}
              selectedSlot={selectedSlot}
              onSelectSlot={setSelectedSlot}
              coach={selectedCoach}
              onContinue={() => setStep("details")}
            />
          )}

          {step === "details" && selectedService && (
            <DetailsStep
              service={selectedService}
              name={name}
              onNameChange={setName}
              email={email}
              onEmailChange={setEmail}
              emailVerified={emailVerified}
              onEmailVerified={() => setEmailVerified(true)}
              phone={phone}
              onPhoneChange={setPhone}
              phoneVerified={phoneVerified}
              onPhoneVerified={() => setPhoneVerified(true)}
              termsAccepted={termsAccepted}
              onTermsAcceptedChange={setTermsAccepted}
              marketingOptIn={marketingOptIn}
              onMarketingOptInChange={setMarketingOptIn}
              couponCode={couponCode}
              onCouponCodeChange={setCouponCode}
              loading={loading}
              onSubmit={handleCreateHold}
            />
          )}

          {step === "payment" && bookingReference && (
            <PaymentStep bookingReference={bookingReference} loading={loading} onPay={handlePay} />
          )}

          {step === "demo_payment" && bookingReference && demoOrder && selectedService && (
            <DemoPaymentStep
              gatewayOrderId={demoOrder.gatewayOrderId}
              bookingReference={bookingReference}
              amountRupees={demoOrder.amountRupees}
              onConfirmed={() => {
                trackPurchase({
                  transactionId: bookingId!,
                  value: selectedService.priceInr,
                  item: {
                    item_id: selectedService.id,
                    item_name: selectedService.name,
                    price: selectedService.priceInr,
                  },
                });
                setStep("confirmed");
              }}
            />
          )}

          {step === "confirmed" && bookingReference && (
            <ConfirmedStep bookingReference={bookingReference} />
          )}
        </motion.div>
      </AnimatePresence>
      </div>
      </div>
      </FormSplitLayout>
      <Footer />
    </>
  );
}
